import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'et-atoms-time-input',
  standalone: true,
  imports: [],
  templateUrl: './time-input.component.html',
  styleUrl: './time-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TimeInputComponent,
      multi: true,
    },
  ],
})
export class TimeInputComponent implements ControlValueAccessor {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  @Input() min?: string;
  @Input() max?: string;
  @Input() step?: string;
  @Input({ transform: booleanAttribute }) required = false;

  value: string | null = null;
  disabled = false;
  focused = false;

  private onChange!: (value: string | null) => void;
  private onTouched!: () => void;

  @HostBinding('class.filled')
  get filled(): boolean {
    return !!this.value;
  }

  @HostBinding('class.focused')
  get isFocused(): boolean {
    return this.focused;
  }

  @HostBinding('class.disabled')
  get isDisabled(): boolean {
    return this.disabled;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value || null;
    this.onChange?.(this.value);
    this.changeDetectorRef.markForCheck();
  }

  onFocus(): void {
    this.focused = true;
    this.changeDetectorRef.markForCheck();
  }

  onBlur(): void {
    this.focused = false;
    this.onTouched?.();
    this.changeDetectorRef.markForCheck();
  }

  writeValue(value: string | null): void {
    this.value = this.normalizeTimeValue(value);
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
  }

  private normalizeTimeValue(value: string | null): string | null {
    if (!value?.trim()) {
      return null;
    }

    const trimmed = value.trim();

    if (/^\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed.slice(0, 5);
    }

    const meridiemMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (meridiemMatch) {
      let hours = Number(meridiemMatch[1]);
      const minutes = meridiemMatch[2];
      const meridiem = meridiemMatch[3].toUpperCase();

      if (meridiem === 'PM' && hours < 12) {
        hours += 12;
      } else if (meridiem === 'AM' && hours === 12) {
        hours = 0;
      }

      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }

    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) {
      return `${String(date.getHours()).padStart(2, '0')}:${String(
        date.getMinutes(),
      ).padStart(2, '0')}`;
    }

    return null;
  }
}
